import * as THREE from 'three'
import type {
  ViewpointBounds,
  ViewpointSceneContext,
  ViewpointSceneElement,
  ViewpointVector3,
} from '../generate-jig.types'
import { isInternalJigNodeReference } from '../jig-internal-nodes'
import type {
  ViewpointVisibilityContext,
  ViewpointVisibilityElement,
  ViewpointVisibilitySample,
} from './visibility-context'

function getObjectName(element: any): string | null {
  return element && element.ElementData && element.ElementData.ObjectName
    ? String(element.ElementData.ObjectName)
    : (element && (element.ObjectName || element.Name) ? String(element.ObjectName || element.Name) : null)
}

function jigQuaternionToThreeQuaternion(rotation: { w: number; x: number; y: number; z: number }): THREE.Quaternion {
  return new THREE.Quaternion(
    -rotation.x,
    -rotation.y,
    rotation.z,
    rotation.w
  )
}

function toThreeQuaternion(rotation: any, treatAsJigStoredQuaternion: boolean): THREE.Quaternion {
  const raw = {
    x: Number(rotation && rotation.x) || 0,
    y: Number(rotation && rotation.y) || 0,
    z: Number(rotation && rotation.z) || 0,
    w: Number.isFinite(rotation && rotation.w) ? Number(rotation.w) : 1,
  }

  if (!treatAsJigStoredQuaternion) {
    return new THREE.Quaternion(raw.x, raw.y, raw.z, raw.w)
  }

  return jigQuaternionToThreeQuaternion({
    w: raw.w,
    x: raw.x,
    y: raw.y,
    z: raw.z,
  })
}

function composeLocalMatrix(element: any, rotatedObjectNames: Set<string>): THREE.Matrix4 | null {
  const objectName = getObjectName(element)
  if (!objectName) {
    return null
  }

  const position = element && element.ElementData && element.ElementData.Position
    ? element.ElementData.Position
    : { x: 0, y: 0, z: 0 }
  const scale = element && element.ElementData && element.ElementData.Scale
    ? element.ElementData.Scale
    : { x: 1, y: 1, z: 1 }
  const threePosition = new THREE.Vector3(
    isInternalJigNodeReference(objectName) ? -Number(position.x || 0) : Number(position.x || 0),
    Number(position.y || 0),
    Number(position.z || 0)
  )
  const threeRotation = toThreeQuaternion(
    element && element.ElementData ? element.ElementData.Rotation : null,
    rotatedObjectNames.has(objectName)
  )
  const threeScale = new THREE.Vector3(
    Number.isFinite(scale && scale.x) ? Number(scale.x) : 1,
    Number.isFinite(scale && scale.y) ? Number(scale.y) : 1,
    Number.isFinite(scale && scale.z) ? Number(scale.z) : 1
  )

  return new THREE.Matrix4().compose(threePosition, threeRotation, threeScale)
}

function buildWorldMatrixResolver(elements: any[], rotatedObjectNames: Set<string>) {
  const localMatrices = new Map<string, THREE.Matrix4>()
  const parentNames = new Map<string, string>()

  elements.forEach(function (element) {
    const objectName = getObjectName(element)
    if (!objectName) {
      return
    }

    const localMatrix = composeLocalMatrix(element, rotatedObjectNames)
    if (!localMatrix) {
      return
    }

    localMatrices.set(objectName, localMatrix)

    const parentName = element && element.ElementData && element.ElementData.ParentName
      ? String(element.ElementData.ParentName)
      : ''
    if (parentName) {
      parentNames.set(objectName, parentName)
    }
  })

  const cache = new Map<string, THREE.Matrix4>()
  const resolving = new Set<string>()

  const resolve = function (objectName: string): THREE.Matrix4 | null {
    const cached = cache.get(objectName)
    if (cached) {
      return cached.clone()
    }

    const localMatrix = localMatrices.get(objectName)
    if (!localMatrix) {
      return null
    }

    if (resolving.has(objectName)) {
      return localMatrix.clone()
    }

    resolving.add(objectName)
    let worldMatrix = localMatrix.clone()
    const parentName = parentNames.get(objectName)

    if (parentName && localMatrices.has(parentName)) {
      const parentWorldMatrix = resolve(parentName)
      if (parentWorldMatrix) {
        worldMatrix = parentWorldMatrix.multiply(localMatrix.clone())
      }
    }

    resolving.delete(objectName)
    cache.set(objectName, worldMatrix.clone())
    return worldMatrix
  }

  return { resolve: resolve }
}

function buildBoundsCorners(bounds: ViewpointBounds): THREE.Vector3[] {
  const halfX = bounds.size.x / 2
  const halfY = bounds.size.y / 2
  const halfZ = bounds.size.z / 2
  const corners: THREE.Vector3[] = []

  ;[-halfX, halfX].forEach(function (x) {
    ;[-halfY, halfY].forEach(function (y) {
      ;[-halfZ, halfZ].forEach(function (z) {
        corners.push(new THREE.Vector3(
          bounds.center.x + x,
          bounds.center.y + y,
          bounds.center.z + z
        ))
      })
    })
  })

  return corners
}

function boundsFromPoints(points: THREE.Vector3[]): ViewpointBounds | null {
  if (!points.length) {
    return null
  }

  const min = new THREE.Vector3(Infinity, Infinity, Infinity)
  const max = new THREE.Vector3(-Infinity, -Infinity, -Infinity)

  points.forEach(function (point) {
    min.min(point)
    max.max(point)
  })

  return {
    center: {
      x: (min.x + max.x) / 2,
      y: (min.y + max.y) / 2,
      z: (min.z + max.z) / 2,
    },
    size: {
      x: max.x - min.x,
      y: max.y - min.y,
      z: max.z - min.z,
    },
  }
}

function transformBounds(
  bounds: ViewpointBounds,
  baselineWorldMatrix: THREE.Matrix4,
  currentWorldMatrix: THREE.Matrix4
): ViewpointBounds | null {
  if (Math.abs(baselineWorldMatrix.determinant()) < 1e-10) {
    return bounds
  }

  const deltaMatrix = currentWorldMatrix.clone().multiply(baselineWorldMatrix.clone().invert())
  const transformedCorners = buildBoundsCorners(bounds).map(function (corner) {
    return corner.applyMatrix4(deltaMatrix)
  })
  return boundsFromPoints(transformedCorners)
}

function transformVector3(point: ViewpointVector3, matrix: THREE.Matrix4): ViewpointVector3 {
  const vector = new THREE.Vector3(point.x, point.y, point.z).applyMatrix4(matrix)
  return { x: vector.x, y: vector.y, z: vector.z }
}

function transformNormal(normal: ViewpointVector3, matrix: THREE.Matrix4): ViewpointVector3 {
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(matrix)
  const vector = new THREE.Vector3(normal.x, normal.y, normal.z).applyMatrix3(normalMatrix)
  const length = vector.length()
  if (length < 1e-10) {
    return { x: 0, y: 0, z: 1 }
  }

  return {
    x: vector.x / length,
    y: vector.y / length,
    z: vector.z / length,
  }
}

function transformVisibilityElement(
  element: ViewpointVisibilityElement,
  deltaMatrix: THREE.Matrix4
): ViewpointVisibilityElement {
  const mesh = element.mesh.clone()
  mesh.matrixAutoUpdate = false
  mesh.matrixWorld.copy(deltaMatrix.clone().multiply(element.mesh.matrixWorld.clone()))
  mesh.matrix.copy(mesh.matrixWorld)
  mesh.updateMatrixWorld(true)

  const samples = element.samples.map(function (sample: ViewpointVisibilitySample) {
    return {
      point: transformVector3(sample.point, deltaMatrix),
      normal: transformNormal(sample.normal, deltaMatrix),
      weight: sample.weight,
    }
  })

  return {
    id: element.id,
    aliases: element.aliases.slice(),
    bounds: boundsFromPoints(samples.map(function (sample) {
      return new THREE.Vector3(sample.point.x, sample.point.y, sample.point.z)
    }).concat(buildBoundsCorners(element.bounds).map(function (corner) {
      return corner.applyMatrix4(deltaMatrix)
    }))) || element.bounds,
    mesh: mesh,
    samples: samples,
  }
}

function buildVisibilityContext(elements: ViewpointVisibilityElement[]): ViewpointVisibilityContext | null {
  if (!elements.length) {
    return null
  }

  const elementMap = new Map<string, ViewpointVisibilityElement>()
  const aliasMap = new Map<string, ViewpointVisibilityElement[]>()

  elements.forEach(function (element) {
    elementMap.set(element.id, element)
    aliasMap.set(element.id, [element])

    element.aliases.forEach(function (alias) {
      const matches = aliasMap.get(alias) || []
      matches.push(element)
      aliasMap.set(alias, matches)
    })
  })

  return {
    elements: elements,
    elementMap: elementMap,
    aliasMap: aliasMap,
  }
}

function combineSceneBounds(elements: ViewpointSceneElement[]): ViewpointBounds | null {
  const allCorners = elements.reduce(function (corners: THREE.Vector3[], element) {
    return corners.concat(buildBoundsCorners(element.bounds))
  }, [])
  return boundsFromPoints(allCorners)
}

export function buildStageViewSceneContext(options: {
  baseSceneContext: ViewpointSceneContext
  baselineSerializedElements?: any[]
  currentSerializedElements?: any[]
  rotatedObjectNames?: string[]
}): ViewpointSceneContext | null {
  const baselineSerializedElements = options.baselineSerializedElements
  const currentSerializedElements = options.currentSerializedElements

  if (!baselineSerializedElements || !baselineSerializedElements.length || !currentSerializedElements || !currentSerializedElements.length) {
    return options.baseSceneContext
  }

  if (!options.baseSceneContext.elements || options.baseSceneContext.elements.length === 0) {
    return options.baseSceneContext
  }

  const rotatedObjectNameSet = new Set(options.rotatedObjectNames || [])
  const baselineResolver = buildWorldMatrixResolver(baselineSerializedElements, new Set<string>())
  const currentResolver = buildWorldMatrixResolver(currentSerializedElements, rotatedObjectNameSet)

  const elements: ViewpointSceneElement[] = []
  const elementBounds = new Map<string, ViewpointBounds>()
  const transformedVisibilityElements: ViewpointVisibilityElement[] = []

  options.baseSceneContext.elements.forEach(function (element) {
    const baselineWorldMatrix = baselineResolver.resolve(element.id)
    const currentWorldMatrix = currentResolver.resolve(element.id)
    const transformedBounds = baselineWorldMatrix && currentWorldMatrix
      ? (transformBounds(element.bounds, baselineWorldMatrix, currentWorldMatrix) || element.bounds)
      : element.bounds

    const nextElement = {
      id: element.id,
      aliases: element.aliases.slice(),
      bounds: transformedBounds,
    }
    elements.push(nextElement)

    nextElement.aliases.forEach(function (alias) {
      elementBounds.set(alias, transformedBounds)
    })

    if (options.baseSceneContext.visibilityContext) {
      const visibilityElement = options.baseSceneContext.visibilityContext.elementMap.get(element.id)
      if (visibilityElement) {
        if (baselineWorldMatrix && currentWorldMatrix) {
          const deltaMatrix = currentWorldMatrix.clone().multiply(baselineWorldMatrix.clone().invert())
          transformedVisibilityElements.push(transformVisibilityElement(visibilityElement, deltaMatrix))
        } else {
          transformedVisibilityElements.push(visibilityElement)
        }
      }
    }
  })

  return {
    sceneBounds: combineSceneBounds(elements) || options.baseSceneContext.sceneBounds,
    elements: elements,
    elementBounds: elementBounds,
    visibilityContext: buildVisibilityContext(transformedVisibilityElements),
    coordinateConvention: options.baseSceneContext.coordinateConvention || null,
  }
}
